import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sparkles, CheckCircle, Calendar, FileText, Award, Download, Info } from 'lucide-react';
import { toast } from 'sonner';
import { ConsentCheckbox, POLICY_VERSION } from '../components/legal/PolicyLayout';

const GST_RATE = 0.18;

const formatINR = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const calcGST = (base) => base * GST_RATE;
const calcTotal = (base) => base + calcGST(base);

const PricingPage = () => {
  const navigate = useNavigate();
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkoutConsent, setCheckoutConsent] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    axios.get(`${API}/payments/status`).then(r => setPaymentsEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const isPaymentOpen = paymentsEnabled;
  const paymentButtonText = isPaymentOpen ? 'Proceed to Payment' : 'Founding Member Registrations Open';

  const handlePayment = async (plan, isFounding) => {
    if (!checkoutConsent) {
      toast.error('Please accept the Terms, Privacy Policy and Data Protection Policy to proceed.');
      return;
    }
    setProcessing(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/order`,
        { plan, is_founding: isFounding, consent_accepted: true, consent_policy_version: POLICY_VERSION },
        { withCredentials: true }
      );
      
      if (!data.enabled) {
        toast.info(data.message || 'Payments are open today — join as a Founding Member.');
        return;
      }
      
      // Razorpay checkout (only reached when payments are live)
      const options = {
        key: data.razorpay_key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        order_id: data.order.id,
        name: 'Plus Ultra Innovation',
        description: data.plan_label,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan,
            }, { withCredentials: true });
            toast.success(`Payment successful! Invoice ${verifyRes.data.invoice_no} sent to your email.`);
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        theme: { color: '#0A1F44' },
      };
      
      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Razorpay checkout not loaded. Please refresh and try again.');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Please sign in to continue.');
        navigate('/login');
      } else {
        toast.error('Failed to initiate payment. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Pricing
  const BUYER_ORIGINAL = 9999;
  const BUYER_FOUNDING = 4999;
  const SUPPLIER_ORIGINAL = 4999;
  const SUPPLIER_FOUNDING = 2499;

  const participationTiers = [
    { range: 'Up to ₹5 Lakh', fee: 500 },
    { range: '₹5L to ₹10L', fee: 1000 },
    { range: '₹10L to ₹25L', fee: 2000 },
    { range: '₹25L to ₹50L', fee: 3000 },
    { range: '₹50L to ₹1 Cr', fee: 4000 },
    { range: 'Above ₹1 Crore', fee: 5000 }
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button onClick={() => navigate('/')} className="text-white/70 hover:text-white" data-testid="pricing-back-btn">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2">
                <Sparkles className="h-7 w-7 text-gold" />
                <div>
                  <div className="text-lg font-bold">ProcureOne Ultra</div>
                  <div className="text-xs text-white/60">by Plus Ultra Innovation</div>
                </div>
              </div>
            </div>
            <button onClick={() => navigate('/login')} className="text-sm text-white/80 hover:text-gold" data-testid="pricing-login-btn">
              Login
            </button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-6xl">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 mb-4 px-4 py-2 rounded-full bg-orange text-white text-xs font-bold tracking-wider uppercase">
            <Calendar className="h-4 w-4" />
            <span>Founding Member Registrations Open</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy mb-3">
            Pricing & <span className="text-gold">Charges</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Transparent yearly subscriptions for buyers and suppliers. All amounts auto-calculate 18% GST at checkout.
          </p>
        </div>

        {/* Subscription Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-6">
          {/* Buyer Subscription */}
          <div className="p-8 rounded-lg border-2 border-gold bg-card shadow-lg" data-testid="buyer-pricing-card">
            <div className="inline-block px-3 py-1 rounded text-xs font-bold uppercase tracking-wider mb-4 bg-gold text-navy">
              Buyer Yearly Subscription
            </div>

            {/* Original Price - struck through */}
            <div className="text-sm text-muted-foreground mb-1">Original Price</div>
            <div className="text-2xl text-muted-foreground line-through mb-1" data-testid="buyer-original-price">
              {formatINR(BUYER_ORIGINAL)} + 18% GST
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              ({formatINR(calcTotal(BUYER_ORIGINAL))} total)
            </div>

            {/* Founding Offer */}
            <div className="inline-block px-2 py-1 rounded text-xs font-bold uppercase tracking-wider mb-2 bg-orange text-white">
              Founding Offer • 50% Off Year 1
            </div>
            <div className="text-sm text-muted-foreground mb-1">Founding Offer Price</div>
            <div className="text-4xl font-bold text-navy mb-1" data-testid="buyer-founding-price">
              {formatINR(BUYER_FOUNDING)}
            </div>
            <div className="text-sm text-muted-foreground mb-4">+ 18% GST</div>

            {/* Breakdown */}
            <div className="p-4 rounded-md bg-muted/50 mb-6 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base price</span>
                <span className="font-medium">{formatINR(BUYER_FOUNDING)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">GST @ 18%</span>
                <span className="font-medium" data-testid="buyer-gst">{formatINR(calcGST(BUYER_FOUNDING))}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between">
                <span className="font-bold text-navy">Final Payable</span>
                <span className="font-bold text-navy text-lg" data-testid="buyer-total">
                  {formatINR(calcTotal(BUYER_FOUNDING))}
                </span>
              </div>
            </div>

            <div className="space-y-2 text-sm mb-6">
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-gold mr-2 mt-0.5 flex-shrink-0" /><span>Unlimited tender postings</span></div>
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-gold mr-2 mt-0.5 flex-shrink-0" /><span>Verified vendor discovery</span></div>
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-gold mr-2 mt-0.5 flex-shrink-0" /><span>Bid comparison & evaluation</span></div>
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-gold mr-2 mt-0.5 flex-shrink-0" /><span>Year 2 — 100% FREE for founding members</span></div>
            </div>

            <button
              disabled={!isPaymentOpen || processing}
              onClick={() => handlePayment('buyer_yearly', true)}
              className={`w-full px-6 py-3 rounded-md font-semibold transition-opacity ${
                isPaymentOpen
                  ? 'bg-navy text-white hover:opacity-90'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
              data-testid="buyer-payment-btn"
            >
              {processing ? 'Processing...' : paymentButtonText}
            </button>
            {!isPaymentOpen && (
              <button onClick={() => navigate('/pre-register?source=founding_member')} className="w-full mt-2 px-6 py-2 rounded-md border border-navy text-navy font-semibold text-sm" data-testid="buyer-prereg-btn">
                Pre-Register Now
              </button>
            )}
          </div>

          {/* Supplier Subscription */}
          <div className="p-8 rounded-lg border-2 border-orange bg-card shadow-lg" data-testid="supplier-pricing-card">
            <div className="inline-block px-3 py-1 rounded text-xs font-bold uppercase tracking-wider mb-4 bg-orange text-white">
              Supplier Yearly Subscription
            </div>

            {/* Original Price - struck through */}
            <div className="text-sm text-muted-foreground mb-1">Original Price</div>
            <div className="text-2xl text-muted-foreground line-through mb-1" data-testid="supplier-original-price">
              {formatINR(SUPPLIER_ORIGINAL)} + 18% GST
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              ({formatINR(calcTotal(SUPPLIER_ORIGINAL))} total)
            </div>

            {/* Founding Offer */}
            <div className="inline-block px-2 py-1 rounded text-xs font-bold uppercase tracking-wider mb-2 bg-orange text-white">
              Founding Offer • 50% Off Year 1
            </div>
            <div className="text-sm text-muted-foreground mb-1">Founding Offer Price</div>
            <div className="text-4xl font-bold text-navy mb-1" data-testid="supplier-founding-price">
              {formatINR(SUPPLIER_FOUNDING)}
            </div>
            <div className="text-sm text-muted-foreground mb-4">+ 18% GST</div>

            {/* Breakdown */}
            <div className="p-4 rounded-md bg-muted/50 mb-6 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base price</span>
                <span className="font-medium">{formatINR(SUPPLIER_FOUNDING)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">GST @ 18%</span>
                <span className="font-medium" data-testid="supplier-gst">{formatINR(calcGST(SUPPLIER_FOUNDING))}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between">
                <span className="font-bold text-navy">Final Payable</span>
                <span className="font-bold text-navy text-lg" data-testid="supplier-total">
                  {formatINR(calcTotal(SUPPLIER_FOUNDING))}
                </span>
              </div>
            </div>

            <div className="space-y-2 text-sm mb-6">
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-orange mr-2 mt-0.5 flex-shrink-0" /><span>Verified supplier profile</span></div>
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-orange mr-2 mt-0.5 flex-shrink-0" /><span>Browse & respond to tenders</span></div>
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-orange mr-2 mt-0.5 flex-shrink-0" /><span>Buyer matchmaking by category & location</span></div>
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-orange mr-2 mt-0.5 flex-shrink-0" /><span>Direct inquiries from buyers</span></div>
              <div className="flex items-start"><CheckCircle className="h-4 w-4 text-orange mr-2 mt-0.5 flex-shrink-0" /><span>Year 2 — 100% FREE for founding members</span></div>
            </div>

            <button
              disabled={!isPaymentOpen || processing}
              onClick={() => handlePayment('supplier_yearly', true)}
              className={`w-full px-6 py-3 rounded-md font-semibold transition-opacity ${
                isPaymentOpen
                  ? 'bg-orange text-white hover:opacity-90'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
              data-testid="supplier-payment-btn"
            >
              {processing ? 'Processing...' : paymentButtonText}
            </button>
            {!isPaymentOpen && (
              <button onClick={() => navigate('/pre-register?source=founding_member')} className="w-full mt-2 px-6 py-2 rounded-md border border-orange text-orange font-semibold text-sm" data-testid="supplier-prereg-btn">
                Pre-Register Now
              </button>
            )}
          </div>
        </div>

        {/* Checkout Consent (DPDP §6 + Consumer Protection Act §2(46)) */}
        {isPaymentOpen && (
          <div className="mb-10 p-4 rounded-md border-2 border-orange/30 bg-orange/5 max-w-2xl mx-auto" data-testid="pricing-consent-wrapper">
            <ConsentCheckbox checked={checkoutConsent} onChange={setCheckoutConsent} testid="pricing-consent" />
            <div className="text-[10px] text-muted-foreground mt-2">
              Consent is required before initiating payment. Your acceptance is logged with timestamp, IP, and policy version <strong>{POLICY_VERSION}</strong>.
            </div>
          </div>
        )}

        {/* Supplier Other Charges */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy mb-2">Supplier Other Charges</h2>
          <p className="text-muted-foreground mb-6">Applicable only on actual tender participation. <strong className="text-green-700">Tender document downloads are 100% FREE.</strong> Pay only when you use.</p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Post-Award */}
            <div className="p-6 rounded-lg border border-border bg-card" data-testid="charge-post-award">
              <Award className="h-8 w-8 text-gold mb-3" />
              <h3 className="text-lg font-semibold text-navy mb-2">Post-Award Charge</h3>
              <div className="text-2xl font-bold text-navy mb-1">0.3%</div>
              <div className="text-xs text-muted-foreground mb-3">of awarded tender value + 18% GST</div>
              <div className="text-xs text-orange font-semibold uppercase tracking-wider">Non-refundable</div>
              <p className="text-sm text-muted-foreground mt-2">
                Charged only after tender is awarded to supplier.
              </p>
            </div>

            {/* Tender Participation */}
            <div className="p-6 rounded-lg border border-border bg-card" data-testid="charge-participation">
              <FileText className="h-8 w-8 text-orange mb-3" />
              <h3 className="text-lg font-semibold text-navy mb-2">Tender Participation Fee</h3>
              <div className="text-2xl font-bold text-navy mb-1">₹500 – ₹5,000</div>
              <div className="text-xs text-muted-foreground mb-3">+ 18% GST per tender (slab-based)</div>
              <p className="text-sm text-muted-foreground mt-2">
                Based on tender value. See table below.
              </p>
            </div>
          </div>

          {/* Participation Fee Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="participation-fee-table">
            <div className="p-4 border-b border-border bg-muted/30">
              <h3 className="font-semibold text-navy">Tender Participation Fee Slabs</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-6 font-medium">Tender Value Range</th>
                    <th className="text-left py-3 px-6 font-medium">Base Fee</th>
                    <th className="text-left py-3 px-6 font-medium">GST (18%)</th>
                    <th className="text-left py-3 px-6 font-medium">Total Payable</th>
                  </tr>
                </thead>
                <tbody>
                  {participationTiers.map((tier, idx) => (
                    <tr key={idx} className="border-b border-border hover:bg-muted/30" data-testid={`tier-row-${idx}`}>
                      <td className="py-3 px-6">{tier.range}</td>
                      <td className="py-3 px-6">{formatINR(tier.fee)}</td>
                      <td className="py-3 px-6 text-muted-foreground">{formatINR(calcGST(tier.fee))}</td>
                      <td className="py-3 px-6 font-semibold text-navy">{formatINR(calcTotal(tier.fee))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="p-6 rounded-lg bg-gold/10 border border-gold/30 flex items-start space-x-3" data-testid="pricing-note">
          <Info className="h-5 w-5 text-navy flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-navy mb-1">Important Note</div>
            <ul className="space-y-1 text-muted-foreground">
              <li>• All charges are <strong className="text-navy">exclusive of 18% GST</strong> unless specifically mentioned.</li>
              <li>• All applicable charges will be <strong className="text-navy">automatically calculated</strong> during checkout.</li>
              <li>• Post-award charges are <strong className="text-navy">non-refundable</strong>. Tender document downloads are <strong className="text-green-700">FREE</strong>.</li>
              <li>• Founding Member subscriptions are open — early access available today.</li>
              <li>• Founding offer (50% off Year 1 + 100% off Year 2) is limited to the first 100 buyers and first 100 suppliers.</li>
              <li>• <strong className="text-navy">Invoices &amp; GST receipts</strong> will be emailed to the registered email address — please keep your email up to date.</li>
            </ul>
          </div>
        </div>

        {/* Company / GST Details for Invoices */}
        <div className="mt-12 p-6 rounded-lg bg-navy text-white" data-testid="company-gst-details">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-bold tracking-wider uppercase text-gold mb-2">Billed By</div>
              <div className="text-xl font-bold mb-1">PLUS ULTRA INNOVATION</div>
              <div className="text-sm text-white/80 mb-1">Proprietor: Amrapali Deepak Sawant</div>
              <div className="text-sm text-white/80">H. No. 903, Manpada, Near Suresh Kirana Stores,</div>
              <div className="text-sm text-white/80">Thane, Maharashtra - 400607</div>
            </div>
            <div>
              <div className="text-xs font-bold tracking-wider uppercase text-gold mb-2">GST &amp; Contact</div>
              <div className="text-sm text-white/80 mb-1"><span className="text-white/60">GSTIN:</span> <span className="font-mono text-gold">27EYMPS1491H1ZT</span></div>
              <div className="text-sm text-white/80 mb-1"><span className="text-white/60">State Code:</span> 27 (Maharashtra)</div>
              <div className="text-sm text-white/80 mb-1"><span className="text-white/60">Registration:</span> Proprietorship</div>
              <div className="text-sm text-white/80 mb-1"><span className="text-white/60">Email:</span> info@plusultrainnovation.com</div>
              <div className="text-sm text-white/80"><span className="text-white/60">WhatsApp:</span> +91 8369211947</div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-4 pt-4 text-xs text-white/60">
            Invoices &amp; GST receipts will be issued under <strong className="text-white">PLUS ULTRA INNOVATION</strong> and emailed to your registered email address.
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold text-navy mb-4">Ready to get started?</h3>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate('/pre-register?source=founding_member')} className="px-6 py-3 rounded-md bg-gold text-navy font-semibold" data-testid="pricing-cta-prereg">
              Pre-Register Interest
            </button>
            <button onClick={() => navigate('/buyer-register')} className="px-6 py-3 rounded-md bg-navy text-white font-semibold" data-testid="pricing-cta-buyer">
              Register as Buyer
            </button>
            <button onClick={() => navigate('/supplier-register')} className="px-6 py-3 rounded-md bg-orange text-white font-semibold" data-testid="pricing-cta-supplier">
              Register as Supplier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
